/**
 * 🔒 VAULT SEAL — Re-sealed May 1, 2026 ("vault upgrade"). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, BookOpen, Headphones, Film, KeyRound, ShieldAlert, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import VaultRequestModal from '../components/VaultRequestModal';
import BuyVaultAccessModal from '../components/BuyVaultAccessModal';
import useUnseenCounts from '../utils/useUnseenCounts';
import CatchUpButton from '../components/CatchUpButton';

/**
 * Secret Vault page.
 *
 * - Always starts LOCKED. Code re-entry is required every visit (and after
 *   every refresh / tab close). Code is held only in component state.
 * - On unlock: lists ebooks, standalone audiobooks, and audiobook series
 *   that an admin has flagged as is_vault=true.
 */
const SecretVault = () => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [hasAccess, setHasAccess] = useState(null); // null=loading, false=denied, true=granted
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [content, setContent] = useState(null); // { ebooks, audiobooks, series, total }
  const [reverifyOpen, setReverifyOpen] = useState(false);
  const [pendingCode, setPendingCode] = useState(''); // remembered while reverifying
  const [buyOpen, setBuyOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { counts: unseenCounts, markSeen: markSeriesSeen, markAllSeen } = useUnseenCounts();

  // Stable per-browser device id used for 1-device-per-code vault binding.
  // Generated once on first vault access, persisted to localStorage. Server
  // never receives the raw id — only the SHA-256 hash is stored on the user
  // doc the first time it successfully unlocks.
  const getDeviceId = () => {
    try {
      let id = localStorage.getItem('vault_device_id');
      if (!id) {
        id = (crypto?.randomUUID && crypto.randomUUID()) ||
          `dev_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
        localStorage.setItem('vault_device_id', id);
      }
      return id;
    } catch {
      return `dev_${Date.now().toString(36)}`;
    }
  };

  // Refresh vault content (e.g. after admin adds a new chapter to a vault series).
  const refreshVault = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/vault/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: isAdmin ? '__admin__' : (pendingCode || code),
          device_id: getDeviceId(),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setContent({
          ebooks: data.ebooks || [],
          audiobooks: data.audiobooks || [],
          series: data.series || [],
          book_series: data.book_series || [],
          sex_stories: data.sex_stories || [],
          total: data.total || 0,
        });
      }
    } catch {/* noop */}
  };

  useEffect(() => {
    (async () => {
      try {
        // Admin shortcut — fetch role first; admins bypass the unlock screen.
        const meRes = await authFetch(`${BACKEND_URL}/api/auth/me`);
        const me = meRes.ok ? await meRes.json() : null;
        if (me?.role === 'admin') {
          setHasAccess(true);
          setIsAdmin(true);
          // Auto-unlock immediately for admins.
          const res = await authFetch(`${BACKEND_URL}/api/vault/access`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: '__admin__', device_id: getDeviceId() }),
          });
          if (res.ok) {
            const data = await res.json();
            setContent({
              ebooks: data.ebooks || [],
              audiobooks: data.audiobooks || [],
              series: data.series || [],
              book_series: data.book_series || [],
              sex_stories: data.sex_stories || [],
              total: data.total || 0,
            });
          }
          return;
        }

        const res = await authFetch(`${BACKEND_URL}/api/vault/status`);
        if (!res.ok) {
          setHasAccess(false);
          return;
        }
        const data = await res.json();
        setHasAccess(!!data.has_access);
      } catch {
        setHasAccess(false);
      }
    })();
  }, [BACKEND_URL]);

  const unlock = async (e, codeOverride) => {
    e?.preventDefault?.();
    const tryCode = (codeOverride ?? code).trim();
    if (!tryCode) {
      setError('Enter your vault code.');
      return;
    }
    setUnlocking(true);
    setError('');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/vault/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: tryCode, device_id: getDeviceId() }),
      });
      const data = await res.json();
      if (res.status === 419 || data?.detail === 'age_reverify_required') {
        // Hold the code in state and pop the reverify modal.
        setPendingCode(tryCode);
        setReverifyOpen(true);
        return;
      }
      if (res.status === 423 || data?.detail === 'device_not_authorized') {
        setError('This vault code is bound to another device. Ask an admin to reset your device, then try again.');
        return;
      }
      if (!res.ok) {
        setError(data.detail || 'Invalid code.');
        return;
      }
      setContent({
        ebooks: data.ebooks || [],
        audiobooks: data.audiobooks || [],
        series: data.series || [],
        book_series: data.book_series || [],
        sex_stories: data.sex_stories || [],
        total: data.total || 0,
      });
      setCode('');
      setPendingCode('');
    } catch {
      setError('Network error — try again.');
    } finally {
      setUnlocking(false);
    }
  };

  const lock = () => {
    setContent(null);
    setError('');
  };

  // ───────────────────────────────────────────────── Loading
  if (hasAccess === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        Loading vault…
      </div>
    );
  }

  // ───────────────────────────────────────────────── Not granted
  if (hasAccess === false) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-slate-900/60 border border-slate-800 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-gray-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Secret Vault</h2>
          <p className="text-sm text-gray-400 mb-6">
            Vault access is invitation-only. Ask an administrator to grant you access — they
            will give you a private code to enter the vault.
          </p>
          <Button
            onClick={() => setBuyOpen(true)}
            className="w-full py-5 text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white mb-3"
            data-testid="vault-locked-buy-btn"
          >
            Buy Vault Access · £20
          </Button>
          <Button
            onClick={() => navigate('/library')}
            variant="outline"
            className="text-gray-300 border-slate-600"
            data-testid="vault-back-btn"
          >
            Back to Library
          </Button>
        </div>
        {buyOpen && (
          <BuyVaultAccessModal onClose={() => setBuyOpen(false)} />
        )}
      </div>
    );
  }

  // ───────────────────────────────────────────────── Locked (re-entry)
  if (!content) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <form
          onSubmit={unlock}
          className="max-w-md w-full bg-slate-900/80 border border-fuchsia-500/30 rounded-2xl p-8 shadow-[0_0_60px_-25px_rgba(217,70,239,0.55)]"
          data-testid="vault-unlock-form"
        >
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 bg-fuchsia-600/20 border border-fuchsia-500/40 rounded-full flex items-center justify-center mb-3">
              <Lock className="w-7 h-7 text-fuchsia-300" />
            </div>
            <h2 className="text-xl font-bold text-white">Secret Vault</h2>
            <p className="text-sm text-gray-400 mt-1">
              Enter your vault code to view the contents.
            </p>
          </div>

          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Vault code"
            autoFocus
            className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono tracking-[0.3em] text-center placeholder:tracking-normal placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500"
            data-testid="vault-code-input"
          />

          {error && (
            <p className="mt-3 text-xs text-red-400 flex items-center justify-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" /> {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={unlocking}
            className="w-full mt-5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="vault-unlock-btn"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {unlocking ? 'Unlocking…' : 'Unlock Vault'}
          </Button>

          <p className="mt-4 text-[11px] text-gray-500 text-center">
            For your security, the code is required every time you open the vault.
          </p>
        </form>
        {reverifyOpen && (
          <VaultRequestModal
            mode="reverify"
            onClose={() => setReverifyOpen(false)}
            onReverified={() => {
              setReverifyOpen(false);
              if (pendingCode) unlock(null, pendingCode);
            }}
          />
        )}
      </div>
    );
  }

  // ───────────────────────────────────────────────── Unlocked
  // Mirror the Library row layout (Journey-Breakdown style) so the Vault
  // reads identically to /library — May 31 / June 2 2026 user spec. Each
  // row gets a fuchsia VAULT ribbon overlay so the locked context stays
  // unmistakable. Tapping opens the reader/player exactly like the
  // standard library does.

  const fmtNum = (n) => Number(n || 0).toLocaleString();
  const fmtMins = (totalSeconds) => {
    const s = Number(totalSeconds || 0);
    if (s <= 0) return '';
    const mins = Math.round(s / 60);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const r = mins % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
  };

  const VAULT_PILL = {
    ebook:       { label: 'Ebook',       cls: 'bg-amber-500/15 text-amber-200 border-amber-400/30', Icon: BookOpen },
    audiobook:   { label: 'Audiobook',   cls: 'bg-purple-500/15 text-purple-200 border-purple-400/30', Icon: Headphones },
    cinematic:   { label: 'Cinematic',   cls: 'bg-pink-500/15 text-pink-200 border-pink-400/30',   Icon: Film },
    text_series: { label: 'Book Series', cls: 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30', Icon: BookOpen },
  };

  const VaultRow = ({ item, kind }) => {
    const P = VAULT_PILL[kind] || VAULT_PILL.ebook;
    const Icon = P.Icon;
    const cover = item.cover_image || item.cover_image_url;
    const coverUrl = cover ? resolveCoverUrl(cover) : null;
    const id = item.series_id || item.book_id || item.content_id;
    const linkTo = kind === 'ebook' ? `/reader/${id}` : null;

    const primary = (kind === 'ebook' || kind === 'text_series')
      ? (item.word_count || item.total_word_count
          ? `${fmtNum(item.word_count || item.total_word_count)}w` : '')
      : fmtMins(item.total_duration || item.duration_seconds);

    const secondary = kind === 'ebook'
      ? (item.author ? `by ${item.author}` : null)
      : `${item.chapter_count || item.episode_count || (item.episodes?.length) || 0} episodes`;

    const free = item.is_free === true || !item.price_amount || Number(item.price_amount) === 0;
    const priceLabel = free ? 'FREE' : `£${Number(item.price_amount || 0).toFixed(2)}`;
    const priceCls = free
      ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
      : 'border-amber-400/40 bg-amber-500/15 text-amber-200';

    const onOpen = () => {
      if (kind === 'ebook') {
        navigate(linkTo);
        return;
      }
      if (kind === 'text_series') {
        // Text-series open the dedicated reader at /text-series/{id}
        // (the route lists episodes; tapping one opens TextSeriesReader).
        if (unseenCounts[id] > 0) markSeriesSeen(id);
        navigate(`/text-series/${id}`);
        return;
      }
      const first = (item.episodes && item.episodes[0]) || (item.chapters && item.chapters[0]) || null;
      const epId = first?.episode_id || first?.chapter_id || item.book_id || id;
      if (unseenCounts[id] > 0) markSeriesSeen(id);
      if (!epId) return;
      navigate(`/player/${epId}`);
    };

    return (
      <button
        type="button"
        onClick={onOpen}
        className="relative w-full text-left flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-fuchsia-500/25 hover:bg-slate-800 hover:border-fuchsia-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/50 transition-colors"
        data-testid={`vault-row-${id}`}
      >
        {/* VAULT ribbon — same fuchsia chip we had before, kept overt so the
            locked context never disappears. */}
        <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-fuchsia-100 bg-fuchsia-600/85 border border-fuchsia-300/40 px-2 py-0.5 rounded-full shadow pointer-events-none">
          <Lock className="w-2.5 h-2.5" /> Vault
        </span>

        <div className="w-10 h-10 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <Icon className="w-5 h-5 text-slate-300" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm truncate">{item.title || 'Untitled'}</p>
          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${P.cls}`}>
              {P.label}
            </span>
            {secondary && <span>{secondary}</span>}
            <span
              className={`px-1.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${priceCls}`}
              data-testid={`vault-row-price-${id}`}
            >
              {priceLabel}
            </span>
            {unseenCounts[id] > 0 && (
              <span
                className="px-1.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider border-fuchsia-400/40 bg-fuchsia-500/20 text-fuchsia-100"
                data-testid={`vault-row-new-${id}`}
              >
                ✨ {unseenCounts[id]} NEW
              </span>
            )}
          </p>
        </div>

        {primary && (
          <div className="text-right tabular-nums flex-shrink-0 pr-6">
            <p className="text-white font-black text-base leading-none">{primary}</p>
          </div>
        )}
      </button>
    );
  };

  const empty = content.total === 0;
  return (
    <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto" data-testid="vault-page">
      {/* Hero band — fuchsia gradient, embossed pattern, big title */}
      <div className="relative overflow-hidden mb-6 -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 sm:py-8 bg-gradient-to-br from-fuchsia-900/55 via-purple-900/35 to-slate-950 border-y border-fuchsia-500/30">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-screen"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)',
            backgroundSize: '8px 8px',
          }}
          aria-hidden="true"
        />
        <div className="relative flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-fuchsia-600/30 border border-fuchsia-400/50 flex items-center justify-center shrink-0 shadow-lg shadow-fuchsia-500/20">
              <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-fuchsia-200" />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-none flex items-center gap-2">
                Secret Vault
              </h1>
              <p className="text-xs sm:text-sm text-fuchsia-200/85 mt-1 truncate">
                {content.total} item{content.total === 1 ? '' : 's'} · private to you · re-enter code per session
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(() => {
              const vaultSeriesIds = (content.series || []).map((s) => s.series_id || s.book_id);
              const vaultIdSet = new Set(vaultSeriesIds);
              const vaultUnseen = Object.entries(unseenCounts)
                .filter(([sid]) => vaultIdSet.has(sid))
                .reduce((sum, [, n]) => sum + (n || 0), 0);
              return (
                <CatchUpButton
                  variant="vault"
                  totalNew={vaultUnseen}
                  onCatchUp={() => markAllSeen(vaultSeriesIds)}
                />
              );
            })()}
            <Button
              onClick={lock}
              variant="outline"
              size="sm"
              className="text-fuchsia-200 border-fuchsia-400/50 bg-fuchsia-500/10 hover:bg-fuchsia-500/25"
              data-testid="vault-lock-btn"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Lock
            </Button>
          </div>
        </div>
      </div>

      {/* Header (legacy block — replaced by hero, kept hidden) */}
      <div className="hidden" aria-hidden="true">
        <div className="flex items-center justify-between mb-6 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-600/20 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-white">Secret Vault</h1>
              <p className="text-xs text-gray-400">
                {content.total} item{content.total === 1 ? '' : 's'} · private to you
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={lock}
              variant="outline"
              size="sm"
              className="text-fuchsia-300 border-fuchsia-500/40 hover:bg-fuchsia-600/20"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Lock
          </Button>
        </div>
      </div>
      </div>

      {empty && (
        <div className="text-center py-16 text-gray-500" data-testid="vault-empty">
          <Lock className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No vault content yet.</p>
          <p className="text-xs mt-1">Ask an admin to add ebooks, audiobooks, or audio series to your vault.</p>
        </div>
      )}

      {/* Single flat list of every vault item — ebooks + standalone
          audiobooks + multi-episode audio series in one chronological
          feed. Mirrors the new Library look exactly. */}
      {!empty && (
        <div className="space-y-2" data-testid="vault-list">
          {/* Feb 12 2026 — Sex Stories rail (18+ themed). Highlights
              vault content tagged with erotica/sex/adult so users can
              jump straight into the niche they came for. */}
          {!!content.sex_stories?.length && (
            <details open className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-2 mb-2" data-testid="vault-section-sex-stories">
              <summary className="cursor-pointer text-pink-200 font-bold uppercase tracking-widest text-xs px-2 py-1">
                Sex Stories ({content.sex_stories.length})
              </summary>
              <div className="space-y-1.5 mt-2">
                {content.sex_stories.map((it) => {
                  const k = it.is_series || it.type === 'text_series' ? 'text_series'
                    : (it.type === 'audiobook' || it.content_type === 'audiobook') ? 'audiobook'
                    : 'ebook';
                  return <VaultRow key={it.series_id || it.book_id || it.content_id} item={it} kind={k} />;
                })}
              </div>
            </details>
          )}
          {/* Book Series — text-series flagged is_vault. Always visible
              when present (separate section so the user can jump right
              into long-form text). */}
          {!!content.book_series?.length && (
            <details open className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-2 mb-2" data-testid="vault-section-book-series">
              <summary className="cursor-pointer text-indigo-200 font-bold uppercase tracking-widest text-xs px-2 py-1">
                Book Series ({content.book_series.length})
              </summary>
              <div className="space-y-1.5 mt-2">
                {content.book_series.map((s) => (
                  <VaultRow key={s.series_id || s.book_id} item={s} kind="text_series" />
                ))}
              </div>
            </details>
          )}
          {content.ebooks.map((it) => (
            <VaultRow key={it.book_id || it.content_id} item={it} kind="ebook" />
          ))}
          {content.audiobooks.map((it) => {
            // Standalone audiobook/cinematic singles.
            const k = (it.type || it.content_type) === 'cinematic' ? 'cinematic' : 'audiobook';
            return <VaultRow key={it.book_id || it.content_id} item={it} kind={k} />;
          })}
          {content.series.map((s) => {
            const k = (s.type || s.content_type) === 'cinematic' ? 'cinematic' : 'audiobook';
            return <VaultRow key={s.series_id || s.book_id} item={s} kind={k} />;
          })}
        </div>
      )}
    </div>
  );
};

export default SecretVault;
